import _ from 'lodash'

/**
 * Replaces common Unicode hyphen-like characters with ASCII hyphen-minus (U+002D).
 * Handles both raw Unicode characters and their percent-encoded forms in URLs.
 *
 * Covered characters:
 *   U+00AD SOFT HYPHEN
 *   U+2010 HYPHEN
 *   U+2011 NON-BREAKING HYPHEN
 *   U+2012 FIGURE DASH
 *   U+2013 EN DASH
 *   U+2014 EM DASH
 *   U+2015 HORIZONTAL BAR
 *   U+2212 MINUS SIGN
 *   U+FE58 SMALL EM DASH
 *   U+FE63 SMALL HYPHEN-MINUS
 *   U+FF0D FULLWIDTH HYPHEN-MINUS
 */
export const normalizeHyphens = (input: string | undefined | null): string | undefined | null => {
  if (!input) return input
  // Replace raw Unicode hyphen-like characters
  let result = input.replace(/[\u00AD\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, '-')
  // Replace their percent-encoded forms (case-insensitive)
  result = result
    .replace(/%C2%AD/gi, '-') // U+00AD SOFT HYPHEN
    .replace(/%E2%80%9[0-5]/gi, '-') // U+2010–U+2015
    .replace(/%E2%88%92/gi, '-') // U+2212 MINUS SIGN
    .replace(/%EF%B9%98/gi, '-') // U+FE58 SMALL EM DASH
    .replace(/%EF%B9%A3/gi, '-') // U+FE63 SMALL HYPHEN-MINUS
    .replace(/%EF%BC%8D/gi, '-') // U+FF0D FULLWIDTH HYPHEN-MINUS
  return result
}

export const checkIsValidUrl = (urlString: string): boolean => {
  try {
    const url = new URL(urlString)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

export const slugIsSafe = (slug: string | undefined | null): boolean => {
  return (slug !== '' && Boolean(slug)) ? /^[a-z0-9-]+$/i.test(slug as string) : false
}

export const checkIsORouteUrl = (url: string | undefined | null): boolean => {
  if (!url) return false
  try {
    const urlObj = new URL(url)
    return urlObj.pathname.startsWith('/o/')
  } catch {
    return false
  }
}

export const checkIsORouteUrlPathname = (pathname: string | undefined | null): boolean => {
  if (!pathname) return false
  return pathname.startsWith('/o/')
}

export const ensureLeadingSlash = (string: string | undefined | null): string | undefined | null => {
  if (!string) return string
  return string.startsWith('/') ? string : `/${string}`
}

export const ensureTrailingSlash = (string: string | undefined | null): string | undefined | null => {
  if (!string) return string
  return string.endsWith('/') ? string : `${string}/`
}

export const removeTrailingSlash = (string: string | undefined | null): string | undefined | null => {
  if (!string) return string
  return string.endsWith('/') ? string.slice(0, -1) : string
}

export const truncateString = (stringToBeTruncated: string | undefined | null, targetLength: number = 450): string => {
  if (!stringToBeTruncated || typeof stringToBeTruncated !== 'string') return ''
  return (
    (stringToBeTruncated?.length > targetLength)
      ? stringToBeTruncated.slice(0, targetLength - 3) + '...'
      : stringToBeTruncated
  )
}

const isBitlyUrl = (urlString: string): boolean => {
  try {
    const urlToParse = urlString.includes('://') ? urlString : `https://${urlString}`
    const {hostname} = new URL(urlToParse)
    return /^(?:www\.)?(?:bit\.ly|j\.mp)$/i.test(hostname)
  } catch {
    return false
  }
}

export const normalizeUrlWithProtocol = (url: string | undefined | null, protocol: string = 'https'): string | null => {
  if (!url) return null

  const trimmedUrl = _.trim(url)
  let normalizedUrl = isBitlyUrl(trimmedUrl)
    ? trimmedUrl
    : ensureTrailingSlash(trimmedUrl) ?? trimmedUrl
  if (!normalizedUrl) return url

  if (normalizedUrl.indexOf('://') !== -1) {
    normalizedUrl = normalizedUrl.substring(normalizedUrl.indexOf('://') + 3)
  }
  let normalizedUrlWithProtocol = normalizedUrl
  if (!/^https:\/\//i.test(normalizedUrl)) {
    normalizedUrlWithProtocol = `${protocol}://${normalizedUrl}`
  }
  return normalizedUrlWithProtocol
}

export const removeSearchQueriesFromUrl = (url: string | undefined | null): string | undefined | null => {
  if (!url) return url
  try {
    const newUrl = new URL(url)
    newUrl.search = ''
    return newUrl.toString()
  } catch (error) {
    console.error('Error in removeSearchQueriesFromUrl', error)
    return url
  }
}

/**
 * Strips zero-width and invisible Unicode format characters that
 * String.trim()/_.trim() leave behind, so values that are visually empty are
 * treated as empty. Covers zero-width spaces/joiners (U+200B-U+200D), the word
 * joiner (U+2060) and BOM (U+FEFF), plus bidirectional format controls: the
 * Arabic letter mark (U+061C), LRM/RLM (U+200E/U+200F), embeddings and
 * overrides (U+202A-U+202E) and isolates (U+2066-U+2069).
 */
export const removeInvisibleCharacters = (value: string): string =>
  value.replace(/[\u061C\u200B-\u200F\u202A-\u202E\u2060\u2066-\u2069\uFEFF]/g, '')

/**
 * Recursively trims whitespace from every string value in a request payload.
 * Strings have invisible/zero-width characters stripped and are then trimmed,
 * arrays and plain objects are walked, and all other values (numbers, booleans,
 * null, undefined) are returned untouched.
 * Used at the API submission boundary so user-entered values never reach the
 * back-end with leading/trailing whitespace or visually-empty content.
 */
export const deepTrim = <T>(value: T): T => {
  if (typeof value === 'string') return _.trim(removeInvisibleCharacters(value)) as unknown as T
  if (Array.isArray(value)) return value.map(item => deepTrim(item)) as unknown as T
  if (_.isPlainObject(value)) {
    return _.mapValues(value as Record<string, unknown>, item => deepTrim(item)) as unknown as T
  }
  return value
}

export const getValidParamValue = (paramName: string | undefined | null): string | null => {
  return (
    paramName
    && paramName !== 'null'
    && paramName !== 'undefined'
    && paramName !== ''
  ) ? paramName : null
}
